import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  createManageEventHeat,
  deleteManageEventHeat,
  listManageEventHeats,
  listManageEventRegistrations,
  updateManageEventHeat,
  type EventRow,
  type HeatRoundType,
  type HeatRow,
  type ManageRegistrationRow,
} from "../lib/api";
import { eventDays, moveInDay, nextOrder, scheduleByDay } from "../lib/eventSchedule";
import { formatEventDate } from "../lib/formatDate";
import { Trash3FillIcon } from "../components/icons";

// Manage → <club> → <event>: the race-day schedule. Each day of the event
// gets its own run order of qualifying heats and mains; each one has a
// "Manage Race" page listing who's registered in that class. Lap/time
// recording comes later.

/** The event's local calendar day for an ISO timestamp ("YYYY-MM-DD"). */
function localDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA");
}

function dayLabel(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

function heatLabel(h: Pick<HeatRow, "className" | "roundType" | "roundNumber">): string {
  return `${h.className} — ${h.roundType === "main" ? "Main" : "Qualifier"} ${h.roundNumber}`;
}

function Breadcrumb({ clubId, ev, extra }: { clubId: string; ev: EventRow; extra?: React.ReactNode }) {
  return (
    <p className="muted" style={{ fontSize: 13 }}>
      <Link to="/manage">Manage</Link> / <Link to={`/manage/${clubId}`}>Events</Link> /{" "}
      {extra ? <Link to={`/manage/${clubId}/events/${ev.eventId}`}>{ev.title}</Link> : ev.title}
      {extra && <> / {extra}</>}
    </p>
  );
}

/** /manage/:clubId/events/:eventId */
export function ManageEventPage() {
  const { clubId = "", eventId = "" } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState<EventRow | null>(null);
  const [heats, setHeats] = useState<HeatRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function refresh() {
    listManageEventHeats(clubId, eventId)
      .then((res) => {
        setEv(res.event);
        setHeats(res.heats);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load the event."));
  }
  useEffect(refresh, [clubId, eventId]);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await action();
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  if (!ev) return error ? <p className="err">{error}</p> : <p>Loading…</p>;

  const days = eventDays(localDay(ev.dateISO), ev.endDateISO ? localDay(ev.endDateISO) : undefined);
  const byDay = scheduleByDay(heats, days);
  const classNames = (ev.classes ?? []).map((c) => c.name);

  function move(dayHeats: HeatRow[], heatId: string, direction: "up" | "down") {
    const changes = moveInDay(dayHeats, heatId, direction);
    if (!changes.length) return;
    run(() => Promise.all(changes.map((c) => updateManageEventHeat(clubId, eventId, c.heatId, { order: c.order }))));
  }

  return (
    <div>
      <Breadcrumb clubId={clubId} ev={ev} />
      <h1>{ev.title}</h1>
      <p className="muted">
        {formatEventDate(ev.dateISO, ev.endDateISO, { withTime: true })}
        {ev.location ? ` · ${ev.location}` : ""}
      </p>
      {error && <p className="err">{error}</p>}
      {!classNames.length && (
        <p className="notice">This event has no classes yet. Add them to the event first, then build the schedule.</p>
      )}

      {days.map((day, i) => {
        const dayHeats = byDay.get(day) ?? [];
        return (
          <section key={day} className="card" style={{ marginBottom: "1rem" }}>
            <h2 style={{ marginTop: 0 }}>
              {days.length > 1 ? `Day ${i + 1}: ` : ""}
              {dayLabel(day)}
            </h2>
            {dayHeats.length === 0 ? (
              <p className="muted">Nothing scheduled yet.</p>
            ) : (
              <div className="table-scroll"><table>
                <thead>
                  <tr><th>#</th><th>Race</th><th aria-label="Actions" /></tr>
                </thead>
                <tbody>
                  {dayHeats.map((h, idx) => (
                    <tr key={h.heatId}>
                      <td>{idx + 1}</td>
                      <td>{heatLabel(h)}</td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button type="button" className="secondary" disabled={busy || idx === 0}
                          title="Move up" aria-label={`Move ${heatLabel(h)} up`}
                          onClick={() => move(dayHeats, h.heatId, "up")}>↑</button>{" "}
                        <button type="button" className="secondary" disabled={busy || idx === dayHeats.length - 1}
                          title="Move down" aria-label={`Move ${heatLabel(h)} down`}
                          onClick={() => move(dayHeats, h.heatId, "down")}>↓</button>{" "}
                        <button type="button" onClick={() => navigate(`/manage/${clubId}/events/${eventId}/heats/${h.heatId}`)}>
                          Manage Race
                        </button>{" "}
                        <button type="button" className="link" disabled={busy}
                          title={`Delete ${heatLabel(h)}`} aria-label={`Delete ${heatLabel(h)}`}
                          onClick={() =>
                            confirm(`Delete ${heatLabel(h)}?`) &&
                            run(() => deleteManageEventHeat(clubId, eventId, h.heatId))
                          }>
                          <Trash3FillIcon />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
            {classNames.length > 0 && (
              <AddHeatForm
                classNames={classNames}
                dayHeats={dayHeats}
                busy={busy}
                onAdd={(body) =>
                  run(() => createManageEventHeat(clubId, eventId, { ...body, dayISO: day, order: nextOrder(dayHeats) }))
                }
              />
            )}
          </section>
        );
      })}
    </div>
  );
}

/** Adds a qualifier or main to the end of one day. The round number
 *  defaults to the next one for that class and type on that day. */
function AddHeatForm({
  classNames,
  dayHeats,
  busy,
  onAdd,
}: {
  classNames: string[];
  dayHeats: HeatRow[];
  busy: boolean;
  onAdd: (body: { className: string; roundType: HeatRoundType; roundNumber: number }) => void;
}) {
  const [className, setClassName] = useState(classNames[0]);
  const [roundType, setRoundType] = useState<HeatRoundType>("qualifier");
  const suggested =
    dayHeats
      .filter((h) => h.className === className && h.roundType === roundType)
      .reduce((max, h) => Math.max(max, h.roundNumber), 0) + 1;
  const [roundNumber, setRoundNumber] = useState<number | "">("");

  return (
    <form
      className="row"
      style={{ marginTop: "0.75rem" }}
      onSubmit={(e) => {
        e.preventDefault();
        onAdd({ className, roundType, roundNumber: roundNumber || suggested });
        setRoundNumber("");
      }}
    >
      <div>
        <label>Class</label>
        <select value={className} onChange={(e) => setClassName(e.target.value)}>
          {classNames.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div style={{ flex: "0 0 auto" }}>
        <label>Type</label>
        <select value={roundType} onChange={(e) => setRoundType(e.target.value as HeatRoundType)}>
          <option value="qualifier">Qualifier</option>
          <option value="main">Main</option>
        </select>
      </div>
      <div style={{ flex: "0 0 6rem" }}>
        <label>Round #</label>
        <input type="number" min={1} placeholder={String(suggested)} value={roundNumber}
          onChange={(e) => setRoundNumber(e.target.value ? Number(e.target.value) : "")} />
      </div>
      <button type="submit" disabled={busy} style={{ flex: "0 0 auto" }}>Add to this day</button>
    </form>
  );
}

/** /manage/:clubId/events/:eventId/heats/:heatId -- everyone registered in
 *  this heat's class. Timing/lap entry comes later. */
export function ManageRacePage() {
  const { clubId = "", eventId = "", heatId = "" } = useParams();
  const [ev, setEv] = useState<EventRow | null>(null);
  const [heat, setHeat] = useState<HeatRow | null>(null);
  const [registrations, setRegistrations] = useState<ManageRegistrationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([listManageEventHeats(clubId, eventId), listManageEventRegistrations(clubId, eventId)])
      .then(([h, r]) => {
        setEv(h.event);
        const found = h.heats.find((x) => x.heatId === heatId) ?? null;
        if (!found) setError("That race isn't on this event's schedule any more.");
        setHeat(found);
        setRegistrations(r.registrations);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load the race."));
  }, [clubId, eventId, heatId]);

  if (error && !ev) return <p className="err">{error}</p>;
  if (!ev || !registrations) return <p>Loading…</p>;

  const racers = heat
    ? registrations
        .filter((r) => r.racerClass.trim().toLowerCase() === heat.className.trim().toLowerCase())
        .sort((a, b) => a.racerName.localeCompare(b.racerName))
    : [];

  return (
    <div>
      <Breadcrumb clubId={clubId} ev={ev} extra={heat ? heatLabel(heat) : "Race"} />
      {error && <p className="err">{error}</p>}
      {heat && (
        <>
          <h1>{heatLabel(heat)}</h1>
          <h2>Registered in {heat.className} ({racers.length})</h2>
          {racers.length === 0 ? (
            <p className="muted">Nobody is registered in this class yet.</p>
          ) : (
            <div className="table-scroll"><table>
              <thead>
                <tr><th>Racer</th><th>Class</th><th>Status</th></tr>
              </thead>
              <tbody>
                {racers.map((r) => (
                  <tr key={r.registrationId}>
                    <td>{r.racerName}</td>
                    <td>{r.racerClass}</td>
                    <td>
                      <span className={`pill ${r.status === "paid" ? "success" : r.status === "pending" ? "warn" : "danger"}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
          <p className="notice" style={{ marginTop: "1rem" }}>
            Recording laps and times for this race is coming next.
          </p>
        </>
      )}
    </div>
  );
}
